<template>
  <div class="payment-success-view">
    <div class="success-content">
      <h1>Payment Successful!</h1>
      <p>You are now a Premium member.</p>

      <!-- Loading state -->
      <div v-if="isLoading" class="loading-section">
        <div class="spinner"></div>
        <p>Loading payment details...</p>
      </div>

      <!-- Payment details -->
      <PaymentDetails
        v-if="paymentDetails"
        :payment-data="paymentDetails"
        @copy-success="handleCopySuccess"
        @copy-error="handleCopyError"
      />

      <button class="button home-btn" @click="returnToApp">Return to the app</button>
      <div class="divider">
        <span>or</span>
      </div>
      <router-link to="/" class="button home-btn">Manage my Premium subscription</router-link>
    </div>

    <!-- Toast notification -->
    <Toast
      :show="toastConfig.show"
      :message="toastConfig.message"
      :type="toastConfig.type"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuth0 } from '@auth0/auth0-vue'
import { setupAuthInterceptor, getPaymentSession, reportError } from '@/utils/api'
import Toast from '@/components/Toast.vue'
import PaymentDetails from '@/components/PaymentDetails.vue'

const route = useRoute()
const router = useRouter()
const { idTokenClaims, getAccessTokenSilently } = useAuth0()

const sessionId = computed(() => route.query.session_id)
const paymentDetails = ref(null)
const isLoading = ref(false)
const toastConfig = ref({
  show: false,
  message: '',
  type: 'success'
})

const showToast = (message, type = 'success', duration = 2000) => {
  toastConfig.value = {
    show: true,
    message,
    type
  }
  setTimeout(() => {
    toastConfig.value.show = false
  }, duration)
}

const handleCopySuccess = (message) => {
  showToast(message)
}

const handleCopyError = (message) => {
  showToast(message, 'error')
}

const returnToApp = () => {
  window.location.replace('cinch://payment-success')
}

// Redirect to home if no session_id query param, otherwise fetch payment details
onMounted(async () => {
  if (!sessionId.value) {
    router.push('/')
    return
  }

  try {
    setupAuthInterceptor()
    isLoading.value = true
    paymentDetails.value = await getPaymentSession(sessionId.value)
  } catch (error) {
    // Silently handle 404 errors (session not found)
    if (error.response?.status === 404) {
      console.log('Payment session not found')
    } else {
      await reportError(error)
      console.error('Failed to fetch payment session:', error)
    }
  } finally {
    isLoading.value = false
  }

  // Poll for role change in background
  try {
    const startTime = Date.now()
    const maxWaitTime = 15000 // 15 seconds
    var currentRoles = idTokenClaims.value?.['https://auth.cinchbudget.com/roles'] || []
    while (!currentRoles.includes('tier_2') && Date.now() - startTime < maxWaitTime) {
      await getAccessTokenSilently({ cacheMode: 'off' })
      currentRoles = idTokenClaims.value?.['https://auth.cinchbudget.com/roles'] || []
      await new Promise((resolve) => setTimeout(resolve, 250))
    }
  } catch (error) {
    await reportError(error, false)
  }
})
</script>

<style scoped>
.payment-success-view {
  padding: 2rem;
  text-align: center;
}

@media (max-width: 450px) {
  .payment-success-view {
    padding: 1rem;
  }
}

.success-content {
  max-width: 500px;
  margin: 0 auto;
}

h1 {
  color: var(--color-heading);
  margin-bottom: 1rem;
  font-size: 2rem;
}

@media (max-width: 450px) {
  h1 {
    font-size: 1.5rem;
  }
}

p {
  margin-bottom: 1rem;
  line-height: 1.6;
}

.home-btn {
  padding: 1rem 2rem;
  font-size: 1.1rem;
  margin-top: 0.5rem;
}

@media (max-width: 450px) {
  .home-btn {
    padding: 0.85rem 1.5rem;
    font-size: 1rem;
  }
}

.loading-section {
  text-align: center;
  margin: 2rem 0;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--color-border);
  border-top: 3px solid var(--color-heading);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 1.25rem 0;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid var(--color-border);
}

.divider span {
  padding: 0 1rem;
  color: var(--color-text);
}

</style>